<template>
  <v-dialog
    :value="visiblePopup"
    @click:outside="onOkClickButton"
    @keydown.esc='onOkClickButton'
    :max-width="400"
  >
    <v-card class="popup-wrapper">
      <div class="title">{{$t(title)}}</div>
      <div class="message">
        <div class='leak-data-popup'>
          <div class='leak-data-popup__data'>
            <v-icon small class="material-icons-outlined">vpn_key</v-icon>{{getFormattedPassword}}
          </div>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge}"
            :value="displayedCopiedBadge"
          >
            <div class='leak-data-popup__actions'>
              <v-btn icon class="copy-btn" target="_blank" @click="copyData()" :ripple="false">
                <v-icon>content_copy</v-icon>
              </v-btn>
            </div>
          </v-badge>
        </div>
        <div class="message__info">
          <v-alert
            text
            dense
            color="teal"
            icon="info"
            border="left"
            class='save-password-message'
          >
            {{$t(message)}}
          </v-alert>
        </div>
        <div class="buttons">
          <v-btn small depressed class="yes-button btn-primary" @click="onOkClickButton()">
            {{$t(okCaption)}}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>

</template>

<script>

import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import {mapGetters} from 'vuex';

export default {
  name: 'NewPasswordPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      displayedCopiedBadge: false,
    };
  },
  computed: {
    ...mapGetters(['newUserPassword', 'resetUserPasswordLoading']),
    title() {
      return 'user.new.password.popup.title';
    },
    message() {
      return 'user.new.password.popup.message';
    },
    okCaption() {
      return 'confirm.ok';
    },
    getFormattedPassword() {
      return this.newUserPassword?.password;
    },
    badgeIcon() {
      return 'build';
    },
    badgeContent() {
      return this.$t('copied');
    },
  },
  methods: {
    onOkClickButton() {
      this.$emit('clicked', true);
    },
    async copyData() {
      const data = this.getFormattedPassword;
      await navigator.clipboard.writeText(data);
      this.displayedCopiedBadge = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.displayedCopiedBadge = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-wrapper {
  max-width: rem(400);
  padding: $space-8 $space-10;
  .title {
    color: var(--text-primary);
    font-size: $font-size-base;
    font-family: $font-family-bold;
    letter-spacing: 1.12px;
    line-height: $space-4;
    text-transform: uppercase;
    padding-bottom: rem(23);
  }
  .message {
    font-size: $font-size-base;
    font-weight: normal;
    color: var(--text-primary);
    letter-spacing: 0;
    line-height: rem(18);
    &__info {
      margin-top: $space-3;
      .save-password-message {
        font-size: $font-size-xs;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $space-4;
      }
    }
    .leak-data-popup{
      display: flex;
      flex-direction: row;
      border-bottom: $border-width-thin var(--border-primary) solid;
      padding-bottom: rem(10);
      padding-top: rem(10);
      justify-content: space-between;
      align-items: center;
      &__data{
        display: flex;
        align-items: center;
        .v-icon {
          margin-right: $space-1-5;
          color: var(--text-secondary);
        }
        font-family: $font-family-medium;
        font-size: $font-size-md;
        color: var(--text-primary);
      }
      &__actions {
        .copy-btn {
          background-color: var(--bg-secondary);
          box-shadow: none;
          letter-spacing: 1.07px;
          line-height: $space-4;
          text-align: center;
          text-transform: uppercase;
          min-width: rem(28);
          max-width: rem(28);
          min-height: rem(28);
          max-height: rem(28);
          color: var(--brand-primary);
          .v-icon {
            font-size: $font-size-md;
          }
          &:hover {
            cursor: pointer;
            border: $border-width-thin solid var(--brand-primary);
            background-color: var(--bg-tertiary);
          }
          &:active{
            border: $border-width-thin solid var(--brand-primary);
            background-color: var(--bg-tertiary);
          }
        }
      }
      .manual-config-badge {
        max-width: fit-content;
        line-height: 1.5;
        &:hover {
          cursor: pointer;
        }
        .copied {
          text-transform: capitalize;
          color: var(--brand-primary);
        }
      }
    }
  }
  .buttons {
    text-align: right;
    .yes-button {
      height: $space-8;
      width: rem(51);
      border-radius: $radius-sm;
      background-color: var(--brand-primary);
      box-shadow: $shadow-sm;
      color: var(--text-inverse);
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;
      text-align: center;
      margin-left: $space-2;
    }
  }
}

</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.popup-wrapper {
  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge {
        background: transparent;
        border: none;
        color: var(--brand-primary);
        .v-icon {
          font-size: $font-size-md;
          transform: rotate(180deg);
        }
      }
    }
    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge {
          text-transform: capitalize;
          background: transparent;
          border: none;
          color: var(--brand-primary);
          right: rem(20);
          inset: auto auto calc(100% - 1px) calc(100% - #{rem(30)});
        }
      }
    }
  }
}
</style>
